import { motion } from 'framer-motion';
import { BookOpen, Users, Target, Heart, Droplets, Building2, TreePine, Handshake } from 'lucide-react';
import { HeritageImagePopup } from '../components/common/HeritageImagePopup';

// Marianne North painting of Marble Rocks
const MARBLE_ROCKS_IMAGE = "https://customer-assets.emergentagent.com/job_4bd2b615-e305-456c-8451-8b16b69acec6/artifacts/dun6105z_Marianne_North_%281830-1890%29_-_Marble_Rocks%2C_Jabalpur%2C_Madhya_Pradesh%2C_India_-_MNS22_-_Royal_Botanic_Gardens%2C_Kew%2C_Library%2C_Art_%5E_Archives.jpg";

// Marble Rocks popup content
const marbleRocksContent = {
  title: "Marble Rocks, Jabalpur (1878)",
  subtitle: "Marianne North (British, 1830–1890)",
  bodyText: [
    "Oil on board. Painted during North's 1877–78 journey through India, this work captures the marble escarpments of Bhedaghat on the Narmada River, upstream of the Dhuandhar Falls.",
    "Courtesy: Marianne North Gallery, Royal Botanic Gardens, Kew (UK)."
  ]
};

const sdgData = [
  {
    number: 6,
    title: 'Clean Water and Sanitation',
    icon: Droplets,
    color: 'bg-cyan-500',
    description: 'Promoting sustainable management of water resources and protection/restoration of water-related ecosystems in heritage contexts.'
  },
  {
    number: 11,
    title: 'Sustainable Cities and Communities',
    icon: Building2,
    color: 'bg-amber-500',
    description: 'Safeguarding cultural and natural heritage sites and integrating heritage conservation with urban resilience and community well-being.'
  },
  {
    number: 15,
    title: 'Life on Land',
    icon: TreePine,
    color: 'bg-green-600',
    description: 'Supporting conservation of terrestrial ecosystems associated with heritage landscapes and traditional water bodies.'
  },
  {
    number: 17,
    title: 'Partnerships for the Goals',
    icon: Handshake,
    color: 'bg-blue-700',
    description: 'Building multi-stakeholder partnerships, including government, civil society, academia, and communities, to strengthen heritage conservation and sustainable development outcomes.'
  }
];

export default function About() {
  return (
    <div className="paper-texture" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-accent font-medium text-sm uppercase tracking-widest">About Us</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mt-4 mb-6">
              About Jabalipuraan
            </h1>
            <p className="text-xl text-primary-foreground/80 leading-relaxed">
              A citizen-led digital heritage platform dedicated to documenting, preserving, 
              and publicly activating the historical, cultural, and social narratives of 
              Jabalpur (historically Jubbulpore), India.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-accent font-medium text-sm uppercase tracking-widest">Our Philosophy</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2 mb-6">
                Heritage-Led Approach
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Jabalipuraan is not just an archive—it's a living platform that makes Jabalpur's 
                  heritage publicly visible and understandable, academically credible, and usable 
                  for civic pride, heritage conservation, skill development, and tourism.
                </p>
                <p>
                  Our work is guided by a simple but powerful principle: we believe heritage 
                  belongs to everyone, and everyone has a role in preserving it.
                </p>
                <blockquote className="font-accent text-xl italic border-l-2 border-accent pl-6 py-2 text-foreground">
                  "Heritage by Jabalpuriyas, for Jabalpuriyas, and of Jabalpuriyas."
                </blockquote>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <HeritageImagePopup
                imageSrc={MARBLE_ROCKS_IMAGE}
                imageAlt="Marble Rocks, Jabalpur (1878) by Marianne North"
                title={marbleRocksContent.title}
                subtitle={marbleRocksContent.subtitle}
                bodyText={marbleRocksContent.bodyText}
                className="w-full h-[400px] shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SDG Alignment Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-accent font-medium text-sm uppercase tracking-widest">Global Impact</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2 mb-4">
              Aligned with UN Sustainable Development Goals
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Jabalipuraan aligns its work with the United Nations Sustainable Development Goals (SDGs) 
              to ensure its impact contributes to global sustainable development.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sdgData.map((sdg, idx) => (
              <motion.div
                key={sdg.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-background p-6 border border-border hover:border-accent/50 transition-colors"
              >
                <div className={`w-14 h-14 ${sdg.color} flex items-center justify-center mb-4`}>
                  <sdg.icon className="h-7 w-7 text-white" />
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-bold text-accent">SDG {sdg.number}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  {sdg.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {sdg.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Convergence Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-accent font-medium text-sm uppercase tracking-widest">Strategic Framework</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2">
                Convergence with Development Programs
              </h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="prose prose-slate max-w-none"
            >
              <p className="text-muted-foreground leading-relaxed mb-6">
                Jabalipuraan emphasizes the convergence of public schemes and development programs 
                with heritage conservation efforts, ensuring that heritage protection contributes 
                to broader national and community development.
              </p>

              <div className="space-y-4">
                {[
                  {
                    title: 'Swachh Bharat Abhiyan',
                    description: 'Promoting cleanliness and sanitation in and around heritage sites and traditional water bodies.'
                  },
                  {
                    title: 'Water Quality Initiatives',
                    description: 'Improving ecological health of heritage ponds, lakes, and reservoirs through water cleanliness programs.'
                  },
                  {
                    title: 'Rural & Urban Development',
                    description: 'Integration with development schemes to support ecological conservation, community engagement, and local livelihoods connected to heritage.'
                  }
                ].map((item, idx) => (
                  <div key={idx} className="bg-muted/30 p-4 border-l-4 border-accent">
                    <h4 className="font-heading font-semibold text-foreground mb-1">{item.title}</h4>
                    <p className="text-muted-foreground text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-accent font-medium text-sm uppercase tracking-widest">Our Values</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2">
              What Guides Us
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Academic Credibility',
                description: 'All content is thoroughly researched with proper citations and references to maintain scholarly standards.'
              },
              {
                icon: Users,
                title: 'Community Ownership',
                description: 'Jabalipuraan is by the people of Jabalpur, ensuring authentic representation of local narratives.'
              },
              {
                icon: Target,
                title: 'Public Accessibility',
                description: 'Making heritage visible and understandable to everyone, while protecting sensitive research.'
              },
              {
                icon: Heart,
                title: 'Civic Pride',
                description: 'Fostering a sense of pride and connection among Jabalpuriyas towards their rich heritage.'
              }
            ].map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-background p-6 border border-border"
              >
                <div className="w-12 h-12 bg-accent/10 flex items-center justify-center mb-4">
                  <value.icon className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-accent font-medium text-sm uppercase tracking-widest">Our Goals</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mt-2">
                What We Aim to Achieve
              </h2>
            </div>

            <div className="space-y-8">
              {[
                {
                  number: '01',
                  title: 'Document & Preserve',
                  description: 'Systematically document the historical, cultural, architectural, and social heritage of Jabalpur for future generations.'
                },
                {
                  number: '02',
                  title: 'Public Activation',
                  description: 'Make heritage accessible and engaging for all—students, researchers, tourists, and residents alike.'
                },
                {
                  number: '03',
                  title: 'Support Conservation',
                  description: 'Provide data and advocacy support for heritage conservation efforts across the city.'
                },
                {
                  number: '04',
                  title: 'Enable Research',
                  description: 'Create a credible resource for academic research on Jabalpur\'s history and culture.'
                },
                {
                  number: '05',
                  title: 'Impact Mapping',
                  description: 'Associate heritage work with measurable SDGs to support strategic planning, impact assessment, and advocacy across heritage and sustainability sectors.'
                }
              ].map((goal, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex gap-6 items-start"
                >
                  <span className="font-heading text-4xl font-bold text-accent/30">
                    {goal.number}
                  </span>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                      {goal.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {goal.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ownership Notice */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-primary-foreground/70">
            All content on Jabalipuraan remains the property of Jabalipuraan. 
            Full admin control is maintained by the founder with no vendor lock-in.
          </p>
        </div>
      </section>
    </div>
  );
}
